import styles from './SMTPDetails.module.css';
import { useForm } from 'react-hook-form';
import ReactEDSWrapper from '../../../utils/react-eds-wrapper';
import { Input, Tooltip, Switch } from '@dsi/react-eds';
import { useTheme } from '../../../hooks/useTheme';
import { useSwobNotifications } from '../../../hooks/NotificationsProvider';
import { BaseSyntheticEvent, useState } from 'react';
import { GeneralSettings, GeneralSettingsRequest } from './Notifications.interfaces';
import api from '../../../api/axiosInstance';

interface SmtpInputs {
  fqdn: string;
  port: number;
  enabled: boolean;
  username: string;
  password: string;
  fromEmail: string;
}

interface Props {
  settings: GeneralSettings | undefined;
}

function SMTPDetails(props: Props) {
  const { theme } = useTheme();
  const { addNotifications } = useSwobNotifications();
  const [showPass, setShowPass] = useState(false);
  const [enabledValue, setEnabledValue] = useState(props.settings?.enabled !== undefined ? props.settings.enabled : true);

  const emailPattern = /^([a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+)$/;

  function getDefaultValues(data: GeneralSettings | undefined): SmtpInputs | undefined {
    if (data) {
      return {
        fqdn: data.smtpIp,
        port: data.smtpPort,
        enabled: data.enabled,
        username: data.smtpUser,
        password: data.smtpPassword,
        fromEmail: data.fromEmail
      };
    }
    return undefined;
  }

  const form = useForm<SmtpInputs>({ defaultValues: getDefaultValues(props.settings) });

  const setGeneralSettingsData = (data: SmtpInputs): GeneralSettingsRequest => {
    return {
      id: props.settings?.id || '',
      customerId: props.settings?.customerId || '',
      customerName: props.settings?.customerName || '',
      country: props.settings?.country || '',
      smtpIp: data.fqdn,
      smtpPort: Number(data.port),
      smtpUser: data.username,
      smtpPassword: data.password,
      fromEmail: data.fromEmail,
      enabled: data.enabled !== undefined ? Boolean(data.enabled) : true
    };
  };

  const resetForm = () => {
    form.reset();
  };

  const onSubmit = async (data: SmtpInputs) => {
    try {
      // TODO: Replace with real save logic (API call, etc.)
      const requestData = setGeneralSettingsData(data);
      await api.post('/api/settings', requestData, { headers: { 'Content-Type': 'application/json' } });
      addNotifications([
        {
          title: 'Success',
          icon: 'icon-success',
          description: 'SMTP settings saved successfully!',
          timestamp: new Date(),
          color: 'green'
        }
      ]);
    } catch (e) {
      console.error(e);
      addNotifications([
        {
          title: 'Error',
          icon: 'icon-triangle-warning',
          description: 'Failed to save SMTP settings.',
          timestamp: new Date(),
          color: 'red'
        }
      ]);
    }
  };

  const handleEnabledChange = (e: BaseSyntheticEvent) => {
    setEnabledValue(e.target.checked);
  };

  return (
    <form className={styles.form} autoComplete="off" onSubmit={form.handleSubmit(onSubmit)}>
      <div
        className={`${styles['settings-container']} ${styles['settingsContainerPadding']} ${theme ? styles['settingsContainerLight'] : styles['settingsContainerDark']}`}
      >
        <div className={styles['settings-title-container']}>
          <div className={styles['settingsTitle']}>SMTP Details</div>
          <p className={styles['scanningDescription']}>
            To enable email notifications, please provide the SMTP server details to ensure succesful delicery of messages.
          </p>
        </div>
        <div className={`${styles.switchContainer} row`}>
          <div className="lg-12">
            <label className={styles.whiteText}>
              Enable/Disable SMTP <span className={styles.requiredAsterisk}> *</span>{' '}
              <ReactEDSWrapper
                component={Tooltip}
                message={enabledValue ? 'Disable SMTP Notifications' : 'Enable SMTP Notifications'}
                pointer
              >
                <span style={{ cursor: 'pointer', marginLeft: 4 }}>
                  <i className="icon icon-info" style={{ fontSize: 16, verticalAlign: 'middle' }}></i>
                </span>
              </ReactEDSWrapper>
            </label>
          </div>
          <div>
            <ReactEDSWrapper
              component={Switch}
              id="enabled"
              value={props.settings?.enabled}
              defaultChecked={enabledValue !== undefined ? enabledValue : true}
              {...form.register('enabled')}
              onChange={handleEnabledChange}
            ></ReactEDSWrapper>
          </div>
        </div>
        <div className={`${styles.formRow} row`}>
          <div className={`lg-3`}>
            <label htmlFor="fqdn" className={styles.whiteText}>
              FQDN/IP <span className={styles.requiredAsterisk}> *</span>{' '}
              <ReactEDSWrapper
                component={Tooltip}
                message="Provide a valid Fully Qualified Domain Name (FQDN) (e.g. stmp.example.com) or the IP address (e.g. 192.168.1.10)"
                pointer
              >
                <span style={{ cursor: 'pointer', marginLeft: 4 }}>
                  <i className="icon icon-info" style={{ fontSize: 16, verticalAlign: 'middle' }}></i>
                </span>
              </ReactEDSWrapper>
            </label>
            <ReactEDSWrapper
              className={form.formState.errors.fqdn ? `${styles.inputField} ${styles.inputError}` : styles.inputField}
              component={Input}
              id="fqdn"
              type="text"
              placeholder="smtp.example.com"
              {...form.register('fqdn', {
                required: 'FQDN/IP is required',
                pattern: {
                  value:
                    /^(?!-)[A-Za-z0-9-]{1,63}(?<!-)\.(?:[A-Za-z]{2,}|[A-Za-z0-9-]{2,}\.[A-Za-z]{2,})$|^(?:(?:[0-9]{1,3}\.){3}[0-9]{1,3})$/,
                  message: 'Please enter a valid FQDN.'
                }
              })}
            />
            {form.formState.errors.fqdn && (
              <span className={styles.error}>
                <span className={styles.dangerIcon} aria-label="error" role="img">
                  &#9888;
                </span>
                {form.formState.errors.fqdn.message || 'Required'}
              </span>
            )}
          </div>
          <div className={`lg-3`}>
            <label htmlFor="port" className={styles.whiteText}>
              Port <span className={styles.requiredAsterisk}> *</span>{' '}
              <ReactEDSWrapper component={Tooltip} message="Provide the SMTP Port Number" pointer>
                <span style={{ cursor: 'pointer', marginLeft: 4 }}>
                  <i className="icon icon-info" style={{ fontSize: 16, verticalAlign: 'middle' }}></i>
                </span>
              </ReactEDSWrapper>
            </label>
            <ReactEDSWrapper
              className={form.formState.errors.port ? `${styles.inputField} ${styles.inputError}` : styles.inputField}
              component={Input}
              id="port"
              type="number"
              placeholder="25"
              {...form.register('port', {
                required: 'Port is required'
              })}
            />
            {form.formState.errors.port && (
              <span className={styles.error}>
                <span className={styles.dangerIcon} aria-label="error" role="img">
                  &#9888;
                </span>
                {form.formState.errors.port.message || 'Required'}
              </span>
            )}
          </div>
        </div>
        <div className={`${styles.formRow} row`}>
          <div className={`lg-3`}>
            <label htmlFor="fromEmail" className={styles.whiteText}>
              From Email <span className={styles.requiredAsterisk}> *</span>{' '}
              <ReactEDSWrapper component={Tooltip} message="Provide the Email to author the messages." pointer>
                <span style={{ cursor: 'pointer', marginLeft: 4 }}>
                  <i className="icon icon-info" style={{ fontSize: 16, verticalAlign: 'middle' }}></i>
                </span>
              </ReactEDSWrapper>
            </label>
            <ReactEDSWrapper
              className={form.formState.errors.fromEmail ? `${styles.inputField} ${styles.inputError}` : styles.inputField}
              component={Input}
              id="fromEmail"
              type="text"
              placeholder="example@mail.com"
              {...form.register('fromEmail', {
                required: 'From Email is required',
                pattern: {
                  value: emailPattern,
                  message: 'Please enter a valid Email.'
                }
              })}
            />
            {form.formState.errors.fromEmail && (
              <span className={styles.error}>
                <span className={styles.dangerIcon} aria-label="error" role="img">
                  &#9888;
                </span>
                {form.formState.errors.fromEmail.message || 'Required'}
              </span>
            )}
          </div>
          <div className={`lg-3`}>
            <label htmlFor="username" className={styles.whiteText}>
              User Name <span className={styles.requiredAsterisk}> *</span>{' '}
              <ReactEDSWrapper component={Tooltip} message="Provide the SMTP User." pointer>
                <span style={{ cursor: 'pointer', marginLeft: 4 }}>
                  <i className="icon icon-info" style={{ fontSize: 16, verticalAlign: 'middle' }}></i>
                </span>
              </ReactEDSWrapper>
            </label>
            <ReactEDSWrapper
              className={form.formState.errors.username ? `${styles.inputField} ${styles.inputError}` : styles.inputField}
              component={Input}
              id="username"
              type="text"
              {...form.register('username', {
                required: 'Username is required'
              })}
            />
            {form.formState.errors.username && (
              <span className={styles.error}>
                <span className={styles.dangerIcon} aria-label="error" role="img">
                  &#9888;
                </span>
                {form.formState.errors.username.message || 'Required'}
              </span>
            )}
          </div>
          <div className={`lg-3`}>
            <label htmlFor="password" className={styles.whiteText}>
              Password <span className={styles.requiredAsterisk}> *</span>{' '}
              <ReactEDSWrapper component={Tooltip} message="Provide the SMTP Password." pointer>
                <span style={{ cursor: 'pointer', marginLeft: 4 }}>
                  <i className="icon icon-info" style={{ fontSize: 16, verticalAlign: 'middle' }}></i>
                </span>
              </ReactEDSWrapper>
            </label>
            <ReactEDSWrapper
              className={form.formState.errors.password ? `${styles.inputField} ${styles.inputError}` : styles.inputField}
              component={Input}
              id="password"
              type={showPass ? 'text' : 'password'}
              icon={`icon ${showPass ? 'icon-invisible' : 'icon-eye'}`}
              onIconClick={() => setShowPass(!showPass)}
              {...form.register('password', {
                required: 'Password is required'
              })}
            />
            {form.formState.errors.password && (
              <span className={styles.error}>
                <span className={styles.dangerIcon} aria-label="error" role="img">
                  &#9888;
                </span>
                {form.formState.errors.password.message || 'Required'}
              </span>
            )}
          </div>
        </div>
        <div className={`${styles['settingsContainerPadding']}`}>
          <div
            className={styles.buttonContainer}
            style={{ width: '100%', display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '1.5rem' }}
          >
            <button type="button" className="btn secondary" onClick={resetForm}>
              Cancel
            </button>
            <button type="submit" className="btn primary">
              Save
            </button>
          </div>
        </div>
      </div>
    </form>
  );
}
export default SMTPDetails;
